import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import i18n, { formatNumber } from '@/utils/i18n';
import { FaSave, FaTrash } from 'react-icons/fa';
import { Communicator } from '../../../../tools/Communication';
import { ContainerType } from './OrderFulfillmentAddContainerTypes';
import ContainerTypeSelector from '../../../../components/ContainerType/ContainerTypeSelector';
import CleanOrDirtyText from '../../../../components/CleanOrDirtyText';
import ContainerTypeDisplay from '../../../../components/ContainerType/ContainerTypeDisplay';
import { useUpdateOrderFulfillmentContainerStationManualAmountMutation } from '../../../../graphql/mutations/updateOrderFulfillmentContainerStationManualAmount.generated';
import { useRemoveOrderFulfillmentContainerStationManualAmountMutation } from '../../../../graphql/mutations/removeOrderFulfillmentContainerStationManualAmount.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';

export type SelectedManualAmountsInContainerStation = {
  order_fulfillment_id: string;
  container_station_id: string;
  manual_amount: {
    container_type_id: number;
    amount: number | undefined;
    amount_sealed: number | undefined;
    old_container_type_id: number;
  };
};

type EditContainerStationModalProps = {
  selectedContainerStationManualAmounts: SelectedManualAmountsInContainerStation;
  packingUnits: Record<number, number>;
  onSubmit: () => void;
  onClose: () => void;
};

const EditContainerStationModal = ({
  selectedContainerStationManualAmounts,
  packingUnits,
  onSubmit,
  onClose,
}: EditContainerStationModalProps) => {
  const { container_station_id, manual_amount, order_fulfillment_id } =
    selectedContainerStationManualAmounts;

  const {
    amount: oldAmount,
    amount_sealed: oldAmountSealed,
    old_container_type_id,
  } = manual_amount;

  const wasSealed = Number(oldAmountSealed) > 0 ? true : false;

  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [selectedContainerTypeId, setSelectedContainerTypeId] = useState(
    old_container_type_id,
  );
  const [isSealed, setIsSealed] = useState(
    Number(oldAmountSealed) > 0 ? true : false,
  );
  const [newAmount, setNewAmount] = useState(oldAmount || oldAmountSealed);
  const [showEditedConfirm, setShowEditedConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [updateContainerStationManualAmount] =
    useUpdateOrderFulfillmentContainerStationManualAmountMutation();

  const [removeOrderFulfillmentContainerStationManualAmount] =
    useRemoveOrderFulfillmentContainerStationManualAmountMutation();

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  const submit = async () => {
    const amount = !isSealed ? newAmount : 0;
    const amount_sealed = isSealed ? newAmount : 0;

    onClose();

    try {
      await updateContainerStationManualAmount({
        variables: {
          order_fulfillment_id: order_fulfillment_id,
          container_station_id: container_station_id,
          manual_amount: {
            amount: Number(amount),
            amount_sealed: Number(amount_sealed),
            container_type_id: selectedContainerTypeId,
            old_container_type_id: Number(old_container_type_id),
          },
        },
      });
    } catch (e: any) {
      if (
        e?.message ===
        'ORDER_FULFILLMENT_HAS_MULTIPLE_ENTRIES_FOR_CONTAINER_STATION'
      ) {
        LayoutStoreInstance.setError(
          i18n.t('OrderFulfillment:ofMultipleContainerStationError'),
        );
      } else {
        LayoutStoreInstance.setError(i18n.t('Common:error'));
      }

      console.error('updateContainerStationManualAmount', e);
    }

    onSubmit();
  };

  const submitDelete = async () => {
    const amount = !isSealed ? newAmount : 0;
    const amount_sealed = isSealed ? newAmount : 0;

    onClose();

    try {
      await removeOrderFulfillmentContainerStationManualAmount({
        variables: {
          order_fulfillment_id: order_fulfillment_id,
          container_station_id: container_station_id,
          manual_amount: {
            amount: Number(amount),
            amount_sealed: Number(amount_sealed),
            container_type_id: old_container_type_id,
            old_container_type_id: Number(old_container_type_id),
          },
        },
      });
    } catch (e: any) {
      if (
        e?.message ===
        'ORDER_FULFILLMENT_HAS_MULTIPLE_ENTRIES_FOR_CONTAINER_STATION'
      ) {
        LayoutStoreInstance.setError(
          i18n.t('OrderFulfillment:ofMultipleContainerStationError'),
        );
      } else {
        LayoutStoreInstance.setError(i18n.t('Common:error'));
      }

      console.error('removeOrderFulfillmentContainerStationManualAmount', e);
    }

    onSubmit();
  };

  return (
    <>
      <Modal isOpen={true} toggle={() => onClose()}>
        <ModalHeader toggle={() => onClose()}>
          <p>{i18n.t('OrderFulfillment:editContainerStationModalTitle')}</p>
        </ModalHeader>

        <ModalBody>
          {i18n.t('OrderFulfillment:editContainerStationCurrent')}:
          <div className="overflow-scroll">
            <Table striped bordered>
              <thead>
                <tr>
                  <th>{i18n.t('Common:containerType')}</th>
                  <th>{i18n.t('Common:amount')}</th>
                  <th>{i18n.t('Common:status')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <ContainerTypeDisplay
                      containerType={containerTypes.find((ct) => {
                        return ct.id === old_container_type_id;
                      })}
                    />
                  </td>
                  <td className="numbers-formatted">
                    {formatNumber(oldAmount || oldAmountSealed || 1)}
                    {packingUnits[old_container_type_id] != null &&
                      `/${formatNumber(packingUnits[old_container_type_id])}`}
                  </td>
                  <td>
                    <CleanOrDirtyText isClean={wasSealed} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          <hr />
          {i18n.t('OrderFulfillment:editContainerStationNew')}:
          <div className="overflow-scroll">
            <Table striped bordered>
              <thead>
                <tr>
                  <th>{i18n.t('Common:containerType')}</th>
                  <th>{i18n.t('Common:amount')}</th>
                  <th>{i18n.t('Common:status')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <ContainerTypeDisplay
                      containerType={containerTypes.find((ct) => {
                        return ct.id === selectedContainerTypeId;
                      })}
                    />
                  </td>
                  <td className="numbers-formatted">
                    {formatNumber(newAmount || 1)}
                    {packingUnits[selectedContainerTypeId] != null &&
                      `/${formatNumber(packingUnits[selectedContainerTypeId])}`}
                  </td>
                  <td>
                    <CleanOrDirtyText isClean={isSealed} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          <hr />
          {i18n.t('OrderFulfillment:editContainerStationModify')}:
          <ContainerTypeSelector
            data={containerTypes.filter((c) => {
              return c.class === 'event_portfolio';
            })}
            value={selectedContainerTypeId.toString()}
            onChange={(value: string | string[] | null) => {
              const containerTypeId = Number(value);
              setSelectedContainerTypeId(containerTypeId);

              const packingUnit = packingUnits[containerTypeId];
              if (packingUnit) {
                setNewAmount(packingUnit);
              }
            }}
            searchable
            clearable
            required
          />
          <br />
          <InputGroup>
            <InputGroupText>{i18n.t('Common:amount')}</InputGroupText>
            <Input
              type="number"
              value={newAmount}
              onChange={(e) => {
                setNewAmount(Number(e.currentTarget.value));
              }}
            />
          </InputGroup>
          <br />
          <ButtonGroup>
            <Button
              color="danger"
              outline
              onClick={() => setIsSealed(false)}
              active={!isSealed}
            >
              {i18n.t('Common:dirty')}
            </Button>
            <Button
              color="primary"
              outline
              onClick={() => setIsSealed(true)}
              active={isSealed}
            >
              {i18n.t('Common:clean')}
            </Button>
          </ButtonGroup>
        </ModalBody>

        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              setShowDeleteConfirm(true);
            }}
          >
            <FaTrash /> {i18n.t('Common:delete')}
          </Button>

          <Button
            color="primary"
            onClick={() => {
              const amountChanged =
                Number(oldAmount || oldAmountSealed) !== Number(newAmount);
              const sealedChanged = wasSealed !== isSealed;
              const containerTypeIdChanged =
                Number(old_container_type_id) !==
                Number(selectedContainerTypeId);

              if (amountChanged || sealedChanged || containerTypeIdChanged) {
                setShowEditedConfirm(true);
              } else {
                onClose();
              }
            }}
          >
            <FaSave /> {i18n.t('Common:save')}
          </Button>

          <Button onClick={() => onClose()} color="secondary">
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      {showEditedConfirm && (
        <Modal
          isOpen={showEditedConfirm}
          toggle={() => {
            setShowEditedConfirm(false);
          }}
        >
          <ModalBody>
            {i18n.t('OrderFulfillment:genericEditModalBody')}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                setShowEditedConfirm(false);
                submit();
              }}
            >
              {i18n.t('Common:yes')}
            </Button>{' '}
            <Button
              color="secondary"
              onClick={() => {
                setShowEditedConfirm(false);
              }}
            >
              {i18n.t('Common:no')}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {showDeleteConfirm && (
        <Modal
          isOpen={showDeleteConfirm}
          toggle={() => {
            setShowDeleteConfirm(false);
          }}
        >
          <ModalBody>
            {i18n.t('OrderFulfillment:genericRemoveModalBody')}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                setShowDeleteConfirm(false);
                submitDelete();
              }}
            >
              {i18n.t('Common:yes')}
            </Button>{' '}
            <Button
              color="secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
              }}
            >
              {i18n.t('Common:no')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default EditContainerStationModal;
